import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";

import { Screen } from "@/components";

import { authorizedAxios } from "@/axios"; // this is to post to moonscape to save access token
import { Callout, Pill } from "@/components/ms";
import { ImportProgressPanel } from "@/components/ImportProgress/ImportProgressPanel";
import { logAxiosError } from "@/utils";
import { useQueryClient } from "@tanstack/react-query";

import {
  useProfileQueryKey,
  useGetLinkedExchangesQueryKey,
} from "@moonscape/shared";

// console.log("[oauth] client_id", client_id)
// console.log("[oauth] client_secret", client_secret)
// console.log("[oauth] redirect_uri", redirect_uri);
/**
 * Extracts the authorization code from the URL.
 *
 * @param location - The location object from react-router.
 *
 * The location object typically has the following properties:
 * - pathname: A string representing the path of the URL.
 * - search: A string representing a query string, including the leading "?" character.
 * - state: The state object, if any, passed to history.push or history.replace when this location was pushed onto the stack. Useful for passing extra information to the route.
 * - hash: A string representing a URL fragment identifier, including the leading "#" character.
 *
 * @returns The authorization code.
 */
export const getCodeFromLocation = ({ location }) => {
  console.log("[oauth] [getCodeFromLocation] location ", location);
  const split = location.toString().split("?");
  if (split.length < 2) {
    return null;
  }
  const pairs = split[1].split("&");
  for (const pair of pairs) {
    const [key, value] = pair.split("=");
    if (key === "code") {
      return decodeURIComponent(value || "");
    }
  }
  return null;
};

export const Pending = ({ children }) => (
  <Callout
    title={
      <span className="flex items-center gap-2.5">
        <span className="h-[7px] w-[7px] animate-pulse rounded-full bg-ms-rule" />
        Linking your Coinbase account…
      </span>
    }
  >
    {children}
  </Callout>
);

export const Error = ({ children }) => (
  <Callout tone="problem" title="Coinbase account linking failed">
    {children}
    <span className="mt-3 block">
      <Pill as={Link} to="/settings/exchange" variant="ghost">
        Go back
      </Pill>
    </span>
  </Callout>
);

export const Success = ({ children }) => (
  <Callout title="Coinbase account linked">
    {children}
    <span className="mt-3 block">
      <Pill as={Link} to="/settings/exchange">
        Go back to Settings
      </Pill>
    </span>
  </Callout>
);

/**
 * Coinbase OAuth2.0 handler component.
 * takes the authorization code from the URL and exchanges it for an access token.
 *
 * for testing - Chrome blocks CORS from localhost - chrome://flags/#block-insecure-private-network-requests
 * see https://stackoverflow.com/a/69464875/427736
 *
 * use this extension and toggle ON
 * https://chrome.google.com/webstore/detail/allow-cors-access-control/lhobafahddgcelffkeicbaginigeejlf?hl=en
 *
 */
export const CoinbaseAuth = () => {
  const location = useLocation();
  const queryClient = useQueryClient();

  console.log("[oauth] location: ", location);
  const [status, setStatus] = useState<"pending" | "success" | "error">(
    "pending"
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { user, getAccessTokenSilently } = useAuth0();

  const exchangeAccessToken = useCallback(
    async (newCode, state) => {
      // Get the CSRF token from local storage
      const csrfToken = localStorage.getItem("csrfToken");

      // If the state parameter doesn't match the CSRF token, throw an error
      if (state !== csrfToken) {
        console.log("[oauth] Invalid CSRF token", state, csrfToken);
      } else {
        console.log("[oauth] csrf token OK");
      }

      try {
        const token = await getAccessTokenSilently();

        const res = await authorizedAxios(token).post(`/oauth/coinbase/token`, {
          code: newCode,
          redirect_uri: process.env.REACT_APP_COINBASE_CLIENT_REDIRECT_URL,
        });

        console.log("[oauth] coinbase authenticated", res);
        setStatus("success");

        // invalidate profile query to refetch the user's profile
        queryClient.invalidateQueries({
          queryKey: [useProfileQueryKey],
        });
        queryClient.invalidateQueries({
          queryKey: [useGetLinkedExchangesQueryKey],
        });
      } catch (error) {
        console.error("[oauth] Error exchanging for access token:", error);

        logAxiosError(error);
        setStatus("error");
        setErrorMessage(
          error?.response?.data?.message ||
            "We couldn't complete the connection. Please try again."
        );
      }
    },
    [getAccessTokenSilently, queryClient]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const newCode = new URLSearchParams(location.search).get("code");
    const state = urlParams.get("state");

    console.log("[oauth] code=", newCode);

    if (newCode) {
      exchangeAccessToken(newCode, state);
    } else {
      // No authorization code in the redirect - Coinbase never handed us
      // one, so there's nothing to exchange. That's a real failure, not
      // "still working on it".
      setStatus("error");
      setErrorMessage("Coinbase didn't return an authorization code.");
    }
  }, []);

  return (
    <div className="min-h-screen flex-1 flex flex-col overflow-hidden">
      <Screen heading="Connect Coinbase">
        <div className="max-w-7xl my-8 mx-auto py-4 px-4 sm:px-6 lg:px-8 flex flex-col gap-4">
          {status === "pending" && (
            <Pending>
              <p>Confirming your Coinbase connection with Moonscape.</p>
            </Pending>
          )}
          {status === "success" && (
            <>
              <Success>
                <p>
                  We're syncing your transactions. Hold tight. You'll soon see
                  them start coming into your Dashboard
                </p>
              </Success>
              <ImportProgressPanel
                exchangeId="coinbase"
                exchangeName="Coinbase"
                variant="inline"
              />
            </>
          )}
          {status === "error" && (
            <Error>
              <p>{errorMessage}</p>
            </Error>
          )}
        </div>
      </Screen>
    </div>
  );
};
